import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, BookOpen, ArrowRight, CheckCircle, ShoppingCart, Sparkles, GraduationCap, Brain } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Kajabi purchase link
const KAJABI_PURCHASE_URL = "https://vittafinance.mykajabi.com/offers/C8mQYkTb";

const ELEMENT_TILES = [
  { id: 'element-1', title: 'Element 1: Regulatory Framework', description: 'Practice questions on CIRO regulations, registration, and compliance', icon: null, color: '#22d3ee' },
  { id: 'element-2', title: 'Element 2: Client Relationships', description: 'KYC, suitability, and client-focused reforms practice', icon: null, color: '#34d399' },
  { id: 'element-3', title: 'Element 3: Scope of Relationships', description: 'Investment advisory roles and responsibilities practice', icon: null, color: '#fbbf24' },
  { id: 'element-4', title: 'Element 4: Client Complaints', description: 'Complaint handling and dispute resolution practice', icon: null, color: '#f87171' },
  { id: 'element-5', title: 'Element 5: Market & Company Analysis', description: 'Economic indicators and securities analysis practice', icon: null, color: '#a78bfa' },
  { id: 'element-6', title: 'Element 6: Market Integrity & Trading', description: 'Trade execution, settlement, and UMIR practice', icon: null, color: '#f472b6' },
  { id: 'element-7', title: 'Element 7: Securities & Products', description: 'Equities, fixed income, and managed products practice', icon: null, color: '#818cf8' },
  { id: 'element-8', title: 'Element 8: Derivatives', description: 'Options, futures, and hedging strategies practice', icon: null, color: '#2dd4bf' },
  { id: 'element-9', title: 'Element 9: Ethics & Conduct', description: 'Conflicts of interest and professional standards practice', icon: null, color: '#fb923c' },
];

const CIREElementQuestionBankSalesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get('token');
  
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const PRICE = 49.99;

  // Check access on mount if token is in URL
  useEffect(() => {
    if (tokenFromUrl) {
      verifyAccess(tokenFromUrl);
    }
  }, [tokenFromUrl]);

  const verifyAccess = async (accessToken) => {
    setCheckingAccess(true);
    setError('');
    
    try {
      const response = await fetch(
        `${API_URL}/api/books/cire-element-bank/check-access?token=${accessToken}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setHasAccess(true);
          setShowAccessForm(false);
          localStorage.setItem('cire_element_bank_token', accessToken);
          setSearchParams({ token: accessToken });
        } else {
          setError('Invalid access code. Please check your email or purchase below.');
        }
      } else {
        setError('Unable to verify access. Please try again.');
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setError('Connection error. Please try again.');
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    
    if (!token) {
      setError('Please enter your access code.');
      return;
    }
    
    verifyAccess(token);
  };

  const handleElementClick = (elementId) => {
    if (!hasAccess) return;
    const url = `/books/cire-element-practice/${elementId}?token=${tokenFromUrl || token}`;
    window.open(url, '_blank');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
    }}>
      {/* JSON-LD Structured Data for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "CIRE Element-Wise Question Bank",
          "description": "900 practice questions across 9 comprehensive elements for CIRE exam preparation.",
          "provider": {
            "@type": "Organization",
            "name": "Vitta Finance Academy",
            "url": "https://www.vittafinance.ca"
          },
          "offers": {
            "@type": "Offer",
            "price": "49.99",
            "priceCurrency": "CAD",
            "availability": "https://schema.org/InStock",
            "url": "https://www.vittafinance.ca/books/cire-element-bank"
          },
          "educationalLevel": "Professional"
        })
      }} />
      
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #4f46e5 100%)',
        padding: '80px 20px 100px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Link to="/cire-exam" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            color: 'rgba(255,255,255,0.7)',
            textDecoration: 'none',
            fontSize: '14px',
            marginBottom: '24px'
          }}>
            ← Back to CIRE Exam
          </Link>
          
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 40px rgba(212,175,55,0.3)'
            }}>
              <BookOpen size={40} color="white" strokeWidth={2.5} />
            </div>
            <div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(212,175,55,0.2)',
                border: '1px solid rgba(212,175,55,0.3)',
                borderRadius: '20px',
                padding: '6px 14px',
                marginBottom: '12px'
              }}>
                <Brain size={14} style={{ color: '#D4AF37' }} />
                <span style={{ color: '#D4AF37', fontSize: '12px', fontWeight: '600' }}>ELEMENT-WISE PRACTICE</span>
              </div>
              <h1 style={{
                fontSize: '42px',
                fontWeight: '800',
                color: 'white',
                lineHeight: 1.2,
                marginBottom: '12px'
              }}>
                CIRE Element-Wise Question Bank
              </h1>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255,255,255,0.8)',
                maxWidth: '600px',
                lineHeight: 1.6,
                marginBottom: '24px'
              }}>
                Master each CIRE element with targeted practice questions. 9 comprehensive question banks with detailed rationales for focused study.
              </p>
              
              {/* Pricing */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <span style={{
                  fontSize: '2.8rem',
                  color: '#D4AF37',
                  fontWeight: 900,
                  lineHeight: 1
                }}>
                  ${PRICE} CAD
                </span>
                <div style={{
                  background: 'rgba(212,175,55,0.25)',
                  border: '2px solid rgba(212,175,55,0.5)',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '0.9rem',
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}>
                  9 Element Banks
                </div>
              </div>
            </div>
          </div>

          {checkingAccess && (
            <div style={{ color: 'rgba(255,255,255,0.6)' }}>Checking access...</div>
          )}
        </div>
      </div>

      {/* Sales Page */}
      {!hasAccess && !showAccessForm && (
        <div style={{
          maxWidth: '1200px',
          margin: '-60px auto 80px',
          padding: '0 20px',
          position: 'relative',
          zIndex: 2
        }}>
          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            marginBottom: '60px',
            flexWrap: 'wrap'
          }}>
            <a
              href={KAJABI_PURCHASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                padding: '18px 36px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(212,175,55,0.4)',
                textDecoration: 'none',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <ShoppingCart size={22} />
              Buy Now - ${PRICE} CAD
            </a>
            <button
              onClick={() => setShowAccessForm(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                padding: '18px 36px',
                background: 'rgba(255,255,255,0.1)',
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
              }}
            >
              <BookOpen size={22} />
              Already Purchased? Access Here
            </button>
          </div>

          {/* Features Section */}
          <div style={{
            background: 'rgba(30,41,59,0.6)',
            borderRadius: '20px',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '40px',
            marginBottom: '40px'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '800',
              color: 'white',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              What's Included
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>9 Element Question Banks</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Comprehensive questions for all 9 CIRE elements</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Detailed Rationales</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Comprehensive explanations for every answer</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Focused Practice</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Target weak areas element by element</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Practice & Test Modes</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Study at your pace or simulate exam conditions</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Performance Tracking</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Track your progress across all elements</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Lifetime Access</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Practice as many times as you need</p>
                </div>
              </div>
            </div>
          </div>

          {/* Element Banks Preview Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '24px'
          }}>
            {ELEMENT_TILES.map((element) => (
              <div
                key={element.id}
                style={{
                  background: 'rgba(30,41,59,0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  padding: '28px',
                  opacity: 0.7,
                  position: 'relative'
                }}
              >
                <div style={{
                  fontSize: '3rem',
                  marginBottom: '16px'
                }}>
                  {element.icon}
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: 'white',
                  marginBottom: '12px'
                }}>
                  {element.title}
                </h3>
                <p style={{
                  fontSize: '0.9rem',
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: 1.6,
                  marginBottom: '16px'
                }}>
                  {element.description}
                </p>
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(212,175,55,0.2)',
                  border: '1px solid rgba(212,175,55,0.4)',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Lock size={14} style={{ color: '#D4AF37' }} />
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#D4AF37',
                    fontWeight: 600
                  }}>
                    Locked
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div style={{
            marginTop: '60px',
            textAlign: 'center',
            padding: '40px',
            background: 'rgba(30,41,59,0.6)',
            borderRadius: '20px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: '800',
              color: 'white',
              marginBottom: '16px'
            }}>
              Ready to Master Each CIRE Element?
            </h2>
            <p style={{
              fontSize: '18px',
              color: 'rgba(255,255,255,0.8)',
              marginBottom: '32px',
              lineHeight: 1.6
            }}>
              Get lifetime access to all 9 element question banks with detailed rationales
            </p>
            <a
              href={KAJABI_PURCHASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                padding: '18px 40px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(212,175,55,0.3)',
                textDecoration: 'none'
              }}
            >
              <ShoppingCart size={22} />
              Get Instant Access - ${PRICE} CAD
            </a>
          </div>
        </div>
      )}

      {/* Access Form */}
      {showAccessForm && !hasAccess && (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 20px' }}>
          <div style={{
            maxWidth: '500px',
            margin: '0 auto',
            background: 'rgba(30,41,59,0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '48px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 24px',
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2.5rem'
              }}>
                <Lock size={48} color="white" />
              </div>
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: 800,
                color: 'white',
                marginBottom: '12px'
              }}>
                Access Your Question Banks
              </h2>
              <p style={{
                fontSize: '1rem',
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 1.6
              }}>
                Enter your access code that was emailed to you after purchase
              </p>
            </div>

            <form onSubmit={handleAccessSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '8px'
                }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(15,23,42,0.6)',
                    color: 'white',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '8px'
                }}>
                  Access Code
                </label>
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Enter your access code"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(15,23,42,0.6)',
                    color: 'white',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
              </div>

              {error && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '10px',
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  color: '#fca5a5',
                  fontSize: '0.9rem',
                  marginBottom: '20px'
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={checkingAccess}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '12px',
                  border: 'none',
                  background: checkingAccess 
                    ? 'rgba(212,175,55,0.5)' 
                    : 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                  color: 'white',
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  cursor: checkingAccess ? 'not-allowed' : 'pointer'
                }}
              >
                {checkingAccess ? 'Verifying...' : 'Access Question Banks'}
              </button>
            </form>

            <div style={{
              marginTop: '24px',
              textAlign: 'center',
              paddingTop: '24px',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
              <p style={{
                fontSize: '0.85rem',
                color: 'rgba(255,255,255,0.6)',
                marginBottom: '12px'
              }}>
                Don't have access yet?
              </p>
              <a
                href={KAJABI_PURCHASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#D4AF37',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 600
                }}
              >
                Purchase the Question Bank →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Elements Grid - Show when has access */}
      {hasAccess && (
        <div style={{
          maxWidth: '1200px',
          margin: '-60px auto 80px',
          padding: '0 20px',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '2px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '40px',
            textAlign: 'center'
          }}>
            <h3 style={{
              color: '#22c55e',
              fontSize: '20px',
              fontWeight: '700',
              marginBottom: '8px'
            }}>
              Access Granted!
            </h3>
            <p style={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: '16px',
              margin: 0
            }}>
              You now have lifetime access to all 9 CIRE element question banks. Good luck with your preparation!
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '24px'
          }}>
            {ELEMENT_TILES.map((element) => (
              <div
                key={element.id}
                onClick={() => handleElementClick(element.id)}
                style={{
                  background: 'rgba(30,41,59,0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  padding: '28px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  position: 'relative'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = 'rgba(212,175,55,0.4)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(212,175,55,0.2)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  fontSize: '3rem',
                  marginBottom: '16px'
                }}>
                  {element.icon}
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: 'white',
                  marginBottom: '12px'
                }}>
                  {element.title}
                </h3>
                <p style={{
                  fontSize: '0.9rem',
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: 1.6,
                  marginBottom: '16px'
                }}>
                  {element.description}
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#D4AF37',
                  fontSize: '0.95rem',
                  fontWeight: 600
                }}>
                  Start Practice →
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CIREElementQuestionBankSalesPage;
